import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import { RefreshCw, Plus, Edit2, Trash2, Download, Calendar, ChevronUp, ChevronDown, Search } from 'lucide-react';
import TablePagination from '../components/TablePagination';

const EX_PAGE_SIZE = 25;

export default function ExchangeRates() {
  const { hasPermission } = usePermissions();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [defaultCurrency, setDefaultCurrency] = useState('');
  const [filters, setFilters] = useState({
    fromCurrency: '',
    toCurrency: '',
    dateFrom: '',
    dateTo: ''
  });
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [exPage, setExPage] = useState(1);

  const canManage = hasPermission('manage_finance_settings');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-600" /> : <ChevronDown className="w-3 h-3 text-blue-600" />;
  };

  const sortedRates = [...rates].sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'FromCurrency':
      case 'ToCurrency':
      case 'Notes':
        return dir * (a[sortField] || '').localeCompare(b[sortField] || '');
      case 'Rate':
        return dir * (parseFloat(a.Rate) - parseFloat(b.Rate));
      case 'EffectiveDate':
        return dir * (new Date(a.EffectiveDate) - new Date(b.EffectiveDate));
      case 'IsActive':
        return dir * ((a.IsActive === b.IsActive) ? 0 : a.IsActive ? -1 : 1);
      default:
        return 0;
    }
  });
  const filteredRates = sortedRates.filter(r => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (r.FromCurrency && r.FromCurrency.toLowerCase().includes(t)) ||
           (r.ToCurrency && r.ToCurrency.toLowerCase().includes(t)) ||
           (r.Notes && r.Notes.toLowerCase().includes(t));
  });
  const pagedRates = filteredRates.slice((exPage - 1) * EX_PAGE_SIZE, exPage * EX_PAGE_SIZE);

  useEffect(() => {
    fetchDefaultCurrency();
    fetchRates();
  }, [filters]);

  const fetchDefaultCurrency = async () => {
    try {
      const response = await apiClient.get('/currencies/company-default');
      setDefaultCurrency(response.data.defaultCurrency);
    } catch (error) {
      console.error('Failed to fetch default currency:', error);
    }
  };

  const fetchRates = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.fromCurrency) params.fromCurrency = filters.fromCurrency;
      if (filters.toCurrency) params.toCurrency = filters.toCurrency;
      if (filters.dateFrom) params.fromDate = filters.dateFrom;
      if (filters.dateTo) params.toDate = filters.dateTo;

      const response = await apiClient.get('/currencies/exchange-rates', { params });
      setRates(response.data.exchangeRates || []);
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestRates = async () => {
    if (!canManage) return;
    
    try {
      setFetching(true);
      const response = await apiClient.post('/currencies/fetch-latest-rates');
      
      alert(`✅ Exchange rates updated!\n\nBase currency: ${response.data.baseCurrency}\nNew rates: ${response.data.inserted}\nUpdated rates: ${response.data.updated}\nEffective date: ${response.data.effectiveDate}`);
      fetchRates();
    } catch (error) {
      console.error('Failed to fetch latest rates:', error);
      alert('❌ Failed to fetch exchange rates from API');
    } finally {
      setFetching(false);
    }
  };

  const handleAddRate = async (rateData) => {
    try {
      await apiClient.post('/currencies/exchange-rates', rateData);
      setShowAddModal(false);
      fetchRates();
    } catch (error) {
      console.error('Failed to add rate:', error);
      alert('Failed to add exchange rate');
    }
  };

  const handleUpdateRate = async (id, rateData) => {
    try {
      await apiClient.put(`/currencies/exchange-rates/${id}`, rateData);
      setEditingRate(null);
      fetchRates();
    } catch (error) {
      console.error('Failed to update rate:', error);
      alert('Failed to update exchange rate');
    }
  };

  const handleDeleteRate = async (id) => {
    if (!confirm('Are you sure you want to delete this exchange rate?')) return;
    
    try {
      await apiClient.delete(`/currencies/exchange-rates/${id}`);
      fetchRates();
    } catch (error) {
      console.error('Failed to delete rate:', error);
      alert('Failed to delete exchange rate');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exchange Rate Management</h1>
          <p className="text-gray-600">
            Manage currency exchange rates for multi-currency invoices
            {defaultCurrency && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Default: {defaultCurrency}
              </span>
            )}
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <button
              onClick={fetchLatestRates}
              disabled={fetching}
              className="btn-secondary flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${fetching ? 'animate-spin' : ''}`} />
              {fetching ? 'Fetching...' : 'Fetch Latest Rates'}
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Rate
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Currency</label>
            <select
              value={filters.fromCurrency}
              onChange={(e) => setFilters({ ...filters, fromCurrency: e.target.value })}
              className="input"
            >
              <option value="">All</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="JPY">JPY</option>
              <option value="CAD">CAD</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Currency</label>
            <select
              value={filters.toCurrency}
              onChange={(e) => setFilters({ ...filters, toCurrency: e.target.value })}
              className="input"
            >
              <option value="">All</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="JPY">JPY</option>
              <option value="CAD">CAD</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="input"
            />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative w-full md:w-80">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); setExPage(1); }}
          placeholder="Search currency or notes…"
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Rates Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('FromCurrency')}><div className="flex items-center gap-1">From<SortIcon field="FromCurrency" /></div></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('ToCurrency')}><div className="flex items-center gap-1">To<SortIcon field="ToCurrency" /></div></th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('Rate')}><div className="flex items-center justify-end gap-1">Rate<SortIcon field="Rate" /></div></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('EffectiveDate')}><div className="flex items-center gap-1">Effective Date<SortIcon field="EffectiveDate" /></div></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('IsActive')}><div className="flex items-center gap-1">Status<SortIcon field="IsActive" /></div></th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('Notes')}><div className="flex items-center gap-1">Notes<SortIcon field="Notes" /></div></th>
              {canManage && (
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={canManage ? 7 : 6} className="px-6 py-4 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : rates.length === 0 ? (
              <tr>
                <td colSpan={canManage ? 7 : 6} className="px-6 py-4 text-center text-gray-500">
                  No exchange rates found
                </td>
              </tr>
            ) : filteredRates.length === 0 ? (
              <tr>
                <td colSpan={canManage ? 7 : 6} className="px-6 py-4 text-center text-gray-500">
                  No results match your search
                </td>
              </tr>
            ) : (
              pagedRates.map((rate) => (
                <tr key={rate.ExchangeRateID} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {rate.FromCurrency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {rate.ToCurrency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-mono text-gray-900">
                    {parseFloat(rate.Rate).toFixed(6)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(rate.EffectiveDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      rate.IsActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {rate.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {rate.Notes || '-'}
                  </td>
                  {canManage && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => setEditingRate(rate)}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRate(rate.ExchangeRateID)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
        <TablePagination page={exPage} pageSize={EX_PAGE_SIZE} total={filteredRates.length} onPageChange={setExPage} />
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingRate) && (
        <RateModal
          rate={editingRate}
          onSave={editingRate ? (data) => handleUpdateRate(editingRate.ExchangeRateID, data) : handleAddRate}
          onClose={() => {
            setShowAddModal(false);
            setEditingRate(null);
          }}
        />
      )}
    </div>
  );
}

function RateModal({ rate, onSave, onClose }) {
  const [formData, setFormData] = useState({
    fromCurrency: rate?.FromCurrency || 'USD',
    toCurrency: rate?.ToCurrency || 'GBP',
    rate: rate?.Rate || '',
    effectiveDate: rate?.EffectiveDate?.split('T')[0] || new Date().toISOString().split('T')[0],
    notes: rate?.Notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {rate ? 'Edit Exchange Rate' : 'Add Exchange Rate'}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Currency</label>
              <select
                value={formData.fromCurrency}
                onChange={(e) => setFormData({ ...formData, fromCurrency: e.target.value })}
                className="input"
                required
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="JPY">JPY</option>
                <option value="CAD">CAD</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Currency</label>
              <select
                value={formData.toCurrency}
                onChange={(e) => setFormData({ ...formData, toCurrency: e.target.value })}
                className="input"
                required
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="JPY">JPY</option>
                <option value="CAD">CAD</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Exchange Rate</label>
              <input
                type="number"
                step="0.000001"
                value={formData.rate}
                onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                className="input"
                placeholder="1.234567"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Effective Date</label>
              <input
                type="date"
                value={formData.effectiveDate}
                onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
                className="input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="input"
                rows="2"
                placeholder="e.g., Manual override, special rate"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {rate ? 'Update' : 'Add'} Rate
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
